import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Button, Tooltip } from '@common/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { localizedStrings } from '@core/strings'
import { ATTACHMENT_THUMBNAIL_WIDTH } from './AttachmentThumbnail'

type AttachmentFrameProps = {
  children: React.ReactElement
  readOnly?: boolean
  onDelete?: () => void
  showFileName?: boolean
  fileName?: string
  size?: 'small' | 'large'
  thumbnailFitToContent?: boolean
  layout?: 'grid' | 'list'
  dataTest?: string
  deleteBtnDataTest?: string
}

export const AttachmentFrame: React.FC<AttachmentFrameProps> = ({
  children,
  readOnly,
  onDelete,
  showFileName,
  fileName,
  size = 'small',
  thumbnailFitToContent,
  layout = 'grid',
  dataTest,
  deleteBtnDataTest,
}) => {
  const showFileNameRow = !!(showFileName && fileName)
  const showDeleteButton = !readOnly && !!onDelete
  const showBottomRow = showFileNameRow || showDeleteButton
  const maxWidth = thumbnailFitToContent
    ? undefined
    : ATTACHMENT_THUMBNAIL_WIDTH[size]

  if (!showBottomRow) {
    return <Box data-test={dataTest}>{children}</Box>
  }

  const fileNameText = showFileNameRow && (
    <Tooltip title={fileName}>
      <Text
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {fileName}
      </Text>
    </Tooltip>
  )

  const deleteButton = showDeleteButton && (
    <Button
      type='button'
      variant='outlined'
      color='error'
      size='small'
      onClick={(e) => {
        e.stopPropagation()
        onDelete()
      }}
      data-test={deleteBtnDataTest}
      endIcon={<DeleteIcon />}
      sx={{ ml: 'auto' }}
    >
      {localizedStrings.delete}
    </Button>
  )

  if (layout === 'list') {
    return (
      <Flex alignItems='center' gap={2} data-test={dataTest}>
        <Box flexShrink={0} width={ATTACHMENT_THUMBNAIL_WIDTH[size]}>
          {children}
        </Box>
        {fileNameText}
        {deleteButton}
      </Flex>
    )
  }

  return (
    <Flex flexDirection='column' maxWidth={maxWidth} data-test={dataTest}>
      {children}
      <Flex mt={1.5} alignItems='center' gap={1}>
        {fileNameText}
        {deleteButton}
      </Flex>
    </Flex>
  )
}
